import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck } from "lucide-react";
import { shortDateTime } from "@/lib/format";
import { motion, AnimatePresence } from "framer-motion";

type Notif = { id: string; kind: string; title: string; body: string | null; created_at: string; read_at: string | null };

export function NotificationsBell() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: notifs = [] } = useQuery({
    queryKey: ["notifications", user?.id],
    enabled: !!user,
    refetchInterval: 3000,
    queryFn: async () => (await supabase.from("notifications").select("*").eq("user_id", user!.id).order("created_at", { ascending: false }).limit(20)).data as Notif[] ?? [],
  });
  const unread = notifs.filter(n => !n.read_at).length;

  const markAll = useMutation({
    mutationFn: async () => {
      await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("user_id", user!.id).is("read_at", null);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative rounded-full">
          <Bell className="h-4 w-4" />
          <AnimatePresence>
            {unread > 0 && (
              <motion.span
                initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--terracotta)] px-1 text-[10px] font-bold text-primary-foreground"
              >
                {unread}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b p-3">
          <div className="flex items-center gap-2">
            <div className="font-display text-base">Notifications</div>
            {unread > 0 && <Badge variant="secondary">{unread} new</Badge>}
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={() => markAll.mutate()}>
              <CheckCheck className="mr-1 h-3 w-3" /> Mark read
            </Button>
          )}
        </div>
        <div className="max-h-[420px] divide-y overflow-auto">
          {notifs.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up ✨</div>}
          {notifs.map(n => (
            <motion.div key={n.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`p-3 ${!n.read_at ? "bg-[color-mix(in_oklab,var(--terracotta)_5%,transparent)]" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold">{n.title}</div>
                {!n.read_at && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--terracotta)]" />}
              </div>
              {n.body && <div className="text-xs text-muted-foreground">{n.body}</div>}
              <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{shortDateTime(n.created_at)}</div>
            </motion.div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
